import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtlasStore } from '../store/atlasStore';
import { UpdateCard } from '../components/UpdateCard';
import { ContextChips } from '../components/ContextChips';
import { PrismElementModal } from '../components/PrismElementModal';
import { AtlasContext, PrismElement } from '../types/atlas';
import { useAuth } from '../contexts/AuthContext';
import { agentNavigationItems, NavContext } from '../components/Navigation';
import '../styles/updates.css';

// Context Selector Component
const ContextSelector: React.FC<{
  availableContexts: Array<{ context: AtlasContext; count: number }>;
  selectedContext: AtlasContext | null;
  onContextSelect: (context: AtlasContext | null) => void;
}> = ({ availableContexts, selectedContext, onContextSelect }) => {
  if (availableContexts.length === 0 || !selectedContext) {
    return null;
  }

  const toTitleCase = (str: string): string => {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getContextLabel = (context: AtlasContext): string => {
    // Handle nested context structure with _element_id_by_dimension_id
    if (context._element_id_by_dimension_id) {
      const entries = Object.entries(context._element_id_by_dimension_id);
      if (entries.length === 0) return 'Default';
      return entries.map(([key, value]) => `${toTitleCase(key)}: ${toTitleCase(String(value))}`).join(', ');
    }
    
    // Fallback for flat structure (filter out keys starting with _)
    const entries = Object.entries(context).filter(([key]) => !key.startsWith('_'));
    if (entries.length === 0) return 'Default';
    return entries.map(([key, value]) => `${toTitleCase(key)}: ${toTitleCase(String(value))}`).join(', ');
  };

  const isSelected = (context: AtlasContext): boolean => {
    return JSON.stringify(context) === JSON.stringify(selectedContext);
  };

  // Analyze dimensions to separate common (non-selectable) from varying (selectable)
  const analyzeContextDimensions = () => {
    if (availableContexts.length === 1) {
      // For single context, extract the mapping properly
      const contextMapping = (selectedContext as any)?._element_id_by_dimension_id || 
        Object.fromEntries(Object.entries(selectedContext || {}).filter(([key]) => !key.startsWith('_')));
      return {
        commonDimensions: contextMapping,
        varyingDimensions: {},
        hasVaryingDimensions: false
      };
    }

    // Extract all dimension-element mappings from all contexts
    const allMappings: Record<string, string>[] = availableContexts.map(item => {
      const context = item.context as any;
      if (context._element_id_by_dimension_id) {
        return context._element_id_by_dimension_id;
      }
      // Fallback for flat structure
      return Object.fromEntries(Object.entries(item.context).filter(([key]) => !key.startsWith('_')));
    });

    if (allMappings.length === 0) {
      return {
        commonDimensions: {},
        varyingDimensions: {},
        hasVaryingDimensions: false
      };
    }

    // Find all unique dimension keys
    const allDimensionKeys = new Set<string>();
    allMappings.forEach(mapping => {
      Object.keys(mapping).forEach(key => allDimensionKeys.add(key));
    });

    // Separate common vs varying dimensions
    const commonDimensions: Record<string, string> = {};
    const varyingDimensions: Record<string, Set<string>> = {};

    allDimensionKeys.forEach(dimensionKey => {
      const values = allMappings.map(mapping => mapping[dimensionKey]).filter(v => v !== undefined);
      const uniqueValues = new Set(values);
      
      if (uniqueValues.size === 1) {
        // All contexts have the same value for this dimension - it's common
        commonDimensions[dimensionKey] = values[0];
      } else {
        // Values differ across contexts - it's varying
        varyingDimensions[dimensionKey] = uniqueValues;
      }
    });

    return {
      commonDimensions,
      varyingDimensions,
      hasVaryingDimensions: Object.keys(varyingDimensions).length > 0
    };
  };

  const { commonDimensions, varyingDimensions, hasVaryingDimensions } = analyzeContextDimensions();

  // Build common dimensions display
  const renderCommonDimensions = () => {
    if (Object.keys(commonDimensions).length === 0) return null;
    
    const commonContext = { _element_id_by_dimension_id: commonDimensions };
    return (
      <div className="context-display">
        <ContextChips context={commonContext} />
      </div>
    );
  };

  // Build varying dimensions selector
  const renderVaryingDimensions = () => {
    if (!hasVaryingDimensions) return null;

    return (
      <div className="context-varying">
        <div className="context-options">
          {availableContexts.map((item, index) => {
            // Build label showing only the varying dimensions (instance names only)
            const varyingEntries = Object.entries(varyingDimensions).map(([dimKey, possibleValues]) => {
              const context = item.context as any;
              const contextMapping: Record<string, string> = context._element_id_by_dimension_id || 
                Object.fromEntries(Object.entries(item.context).filter(([key]) => !key.startsWith('_')));
              const value = contextMapping[dimKey];
              return value ? toTitleCase(value) : null;
            }).filter(Boolean);
            
            const varyingLabel = varyingEntries.join(', ');
            
            return (
              <button
                key={index}
                className={`context-option ${isSelected(item.context) ? 'selected' : ''}`}
                onClick={() => onContextSelect(item.context)}
                title={getContextLabel(item.context)}
              >
                <span className="context-text">{varyingLabel}</span>
                <span className="context-count">({item.count})</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="context-selector">
      {renderCommonDimensions()}
      {renderVaryingDimensions()}
    </div>
  );
};

// ItemsContent component implementation
const ItemsContent: React.FC<{
  onContextSelect: (context: AtlasContext | null) => void;
  selectedContext: AtlasContext | null;
  editMode: boolean;
  onEditElement: (element: PrismElement) => void;
  searchQuery: string;
}> = ({ onContextSelect, selectedContext, editMode, onEditElement, searchQuery }) => {
  const {
    itemElements,
    isLoading,
    error,
    setCurrentItemsDimension,
    elementProfileMap,
    currentItemsDimension
  } = useAtlasStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [itemsWithProfiles, setItemsWithProfiles] = useState<Set<string>>(new Set());

  const getNavContext = (): NavContext | undefined => {
    if (location.state?.navContext) {
      return location.state.navContext;
    }
    const dimensionFqn = location.state?.dimensionFqn || location.state?.hint || currentItemsDimension;
    if (!dimensionFqn) {
      return undefined;
    }
    const matchingAgent = agentNavigationItems.find(item => item.hint === dimensionFqn);
    if (!matchingAgent) {
      return undefined;
    }
    return {
      section: 'agent',
      path: matchingAgent.path,
      hint: matchingAgent.hint,
      label: matchingAgent.label
    };
  };

  useEffect(() => {
    const dimensionFqn = location.state?.dimensionFqn || location.state?.hint;
    if (dimensionFqn) {
      setCurrentItemsDimension(dimensionFqn);
    }
  }, [location.state?.dimensionFqn, location.state?.hint, setCurrentItemsDimension]);

  // Check which items have profiles for the current context using store data
  useEffect(() => {
    const checkProfiles = () => {
      const profileSet = new Set<string>();
      
      itemElements.forEach(element => {
        const elementProfiles = elementProfileMap.get(element.fqn) || [];
        
        if (elementProfiles.length > 0) {
          if (selectedContext) {
            // Check if there's a profile for the selected context
            const contextFilteredProfiles = elementProfiles.filter(profile => 
              JSON.stringify(profile.context || {}) === JSON.stringify(selectedContext)
            );
            if (contextFilteredProfiles.length > 0) {
              profileSet.add(element.fqn);
            }
          } else {
            // If no context is selected, any profile counts
            profileSet.add(element.fqn);
          }
        }
      });
      
      setItemsWithProfiles(profileSet);
    };

    if (itemElements.length > 0) {
      checkProfiles();
    }
  }, [itemElements, selectedContext, elementProfileMap]);

  const handleProfileClick = async (targetFqn: string) => {
    // Don't navigate if the item doesn't have a profile for the current context mode
    if (!itemsWithProfiles.has(targetFqn)) {
      return;
    }

    try {
      const profiles = elementProfileMap.get(targetFqn) || [];
      if (profiles.length > 0) {
        // Filter profiles by selected context if one is selected
        let targetProfile = profiles[0]; // Default to first profile
        
        if (selectedContext && profiles.length > 1) {
          const contextFilteredProfiles = profiles.filter(profile => 
            JSON.stringify(profile.context || {}) === JSON.stringify(selectedContext)
          );
          if (contextFilteredProfiles.length > 0) {
            targetProfile = contextFilteredProfiles[0];
          }
        }
        
        // Single encode to handle special characters like '/' and '@'
        const encodedFqn = encodeURIComponent(targetProfile.fqn);
        const navContext = getNavContext();
        const dimensionFqn = location.state?.dimensionFqn || location.state?.hint || currentItemsDimension || navContext?.hint;
        navigate(`/viewer/${encodedFqn}`, {
          state: {
            fromView: 'items',
            label: location.state?.label || navContext?.label,
            hint: navContext?.hint || dimensionFqn,
            dimensionFqn,
            navContext
          }
        });
      }
    } catch (error) {
      console.error('Failed to navigate to profile:', error);
    }
  };

  // Filter items based on search, showAll toggle, and profile availability
  const getDisplayItems = () => {
    let items = itemElements;

    // Apply search filter (case-insensitive)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(element =>
        element.name.toLowerCase().includes(query) ||
        (element.description && element.description.toLowerCase().includes(query))
      );
    }

    if (editMode) {
      return items; // Show all items (active and inactive) in edit mode
    }

    // In non-edit mode, always suppress items that do not have a profile
    // in the currently selected context mode.
    return items.filter(element => element.is_active && itemsWithProfiles.has(element.fqn));
  };

  if (isLoading.dimensions || isLoading.itemElements) {
    return (
      <div className="content-panel">
        <div className="updates-loading">
          <div className="loading-skeleton" />
          <div className="loading-skeleton" />
          <div className="loading-skeleton" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-panel">
        <div className="updates-error">
          <h3>Failed to load items</h3>
          <p>{typeof error === 'string' ? error : 'An unexpected error occurred'}</p>
          <button onClick={() => location.state?.dimensionFqn && setCurrentItemsDimension(location.state.dimensionFqn)}>Retry</button>
        </div>
      </div>
    );
  }

  const displayItems = getDisplayItems();

  if (!displayItems.length) {
    return (
      <div className="content-panel">
        <div className="updates-empty">
          {searchQuery.trim() ? (
            <>
              <h3>No matches found</h3>
              <p>No items match "{searchQuery}". Try a different search term or press ESC to clear.</p>
            </>
          ) : (
            <>
              <h3>No profiles available</h3>
              <p>No profiles have been created for items in this dimension yet</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="content-panel">
      <div className="updates-grid">
        {displayItems
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((element) => {
            const hasProfile = itemsWithProfiles.has(element.fqn);
            const isInactive = !element.is_active;
            
            return (
              <UpdateCard
                key={element.fqn}
                title={element.name}
                description={element.description}
                image_id={element.hero_image_id}
                resource_fqn={element.fqn}
                onProfileClick={editMode ? () => onEditElement(element) : () => handleProfileClick(element.fqn)}
                viewType="items"
                disabled={editMode ? false : !hasProfile}
                isInactive={isInactive}
              />
            );
          })}
      </div>
    </div>
  );
};

export const UpdatesView: React.FC = () => {
  const location = useLocation();
  const scrollPositionRef = useRef(0);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    element?: PrismElement;
  }>({
    isOpen: false,
    mode: 'create',
  });

  const { user } = useAuth();
  const isAdmin = user?.roles.includes('admin') || false;

  // Ensure non-admin users cannot be in edit mode
  useEffect(() => {
    if (!isAdmin && editMode) {
      setEditMode(false);
    }
  }, [isAdmin, editMode]);

  const { 
    setCurrentItemsDimension,
    availableContexts,
    selectedContext,
    setSelectedContext,
    fetchItemElements
  } = useAtlasStore();

  // Set dimension FQN when component mounts or when returning from profile
  useEffect(() => {
    // If returning from profile, use the stored dimension
    if (location.state?.fromProfile && (location.state?.dimensionFqn || location.state?.hint)) {
      setCurrentItemsDimension(location.state.dimensionFqn || location.state.hint);
    }
    // If navigating to this view with a hint
    else if (location.state?.hint || location.state?.dimensionFqn) {
      setCurrentItemsDimension(location.state.dimensionFqn || location.state.hint);
    }
  }, [setCurrentItemsDimension, location.state]);

  // Save scroll position before navigation
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position when returning to this view
  useEffect(() => {
    if (location.state?.fromProfile) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: 'auto'
        });
      });
    }
  }, [location]);

  // Handle context selection
  const handleContextSelect = (context: AtlasContext | null) => {
    setSelectedContext(context);
    // Note: No need to re-fetch elements since we now keep all elements loaded
  };

  // Modal handlers
  const handleCreateElement = () => {
    if (!isAdmin) {
      console.warn('Create element action attempted by non-admin user');
      return;
    }
    setModalState({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEditElement = (element: PrismElement) => {
    if (!isAdmin) {
      console.warn('Edit element action attempted by non-admin user');
      return;
    }
    setModalState({
      isOpen: true,
      mode: 'edit',
      element,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      mode: 'create',
    });
  };

  const handleElementSuccess = (element: PrismElement) => {
    // Refresh the items data
    const dimensionFqn = location.state?.dimensionFqn || location.state?.hint;
    if (dimensionFqn) {
      fetchItemElements(dimensionFqn);
    }
  };

  // Get the title from navigation state
  const getTitle = () => {
    return location.state?.label || 'Knowledge';
  };

  return (
    <div className="updates-view">
      <div className="main-content overview-mode">
        <div className="profile-header profile-header-grid">
          <div className="header-left">
            <h1>{getTitle()}</h1>
            <ContextSelector
              availableContexts={availableContexts}
              selectedContext={selectedContext}
              onContextSelect={handleContextSelect}
            />
          </div>
          <div className="header-controls">
            {isAdmin && (
              <>
                <label className="show-all-toggle">
                  <input
                    type="checkbox"
                    checked={editMode}
                    onChange={(e) => setEditMode(e.target.checked)}
                  />
                  <div className="toggle-slider">
                    <div className="toggle-thumb"></div>
                  </div>
                  <span>Edit</span>
                </label>
                <button 
                  className="new-item-button" 
                  onClick={handleCreateElement}
                >
                  New
                </button>
              </>
            )}
          </div>
        </div>
        {/* Search bar - full width below header */}
        <div className="search-bar-row">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearchQuery(''); }}
            placeholder="Search title and description…"
            style={{ padding: '6px 10px', border: '1px solid #D0D5DD', borderRadius: 6, minWidth: 260, height: 36 }}
          />
        </div>
        <ItemsContent onContextSelect={handleContextSelect} selectedContext={selectedContext} editMode={isAdmin && editMode} onEditElement={handleEditElement} searchQuery={searchQuery} />
        
        {isAdmin && (
          <PrismElementModal
            isOpen={modalState.isOpen}
            onClose={handleCloseModal}
            onSuccess={handleElementSuccess}
            mode={modalState.mode}
            dimensionFqn={location.state?.dimensionFqn || location.state?.hint}
            element={modalState.element}
          />
        )}
      </div>
    </div>
  );
}; 
